import natsort from 'natsort';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, List, ListItemButton, ListItemText, Tooltip, Typography, useTheme } from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import { boldString } from "../Utils";
import { EDITABLE_LAYERS, NO_EDITABLE_LAYERS, POSES_LAYER } from '../tools/useLayout';
import FallbackInformation from './FallbackInformation';

/**
 * Renders a single item in the sub-list of layers (e.g., a lineal or aisle side).
 * It displays the item's name and provides controls for editing, deleting, and toggling visibility.
 * @param {object} props - The component props.
 * @param {object} props.layer - The parent layer data.
 * @param {string} props.item - The name of the child item (e.g., aisle name).
 * @param {string} props.subLayer - The name of the sub-layer item (e.g., lineal name).
 * @param {function} props.deleteAnnotation - Function to delete the annotation.
 */
function LayerChildChildItem({ layer, item, subLayer, deleteAnnotation, setDialogEdit, toggleSubChildLayer }) {
    const [isHovered, setIsHovered] = useState(false);
    const handleEdit = useCallback(() => {
        setDialogEdit({
            layer: EDITABLE_LAYERS.AISLE_SIDE,
            item: item,
            data: {
                name: subLayer,
                parent: item,
            },
        });
    }, [item, subLayer, setDialogEdit]);
    const isEyeVisible = layer?.layers?.[item]?.subLayers?.[subLayer]?.visible;

    return <ListItemButton dense sx={{ pl: 10 }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        {/* Aisle-lineal name */}
        <ListItemText primary={subLayer} />
        {/* Icons */}
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
            {/* Edit icon button */}
            <IconButton size='small' onClick={handleEdit} sx={{ visibility: isHovered ? 'visible' : 'hidden' }}>
                <IconComponent iconName='create-outline' />
            </IconButton>
            {/* Trash icon button */}
            <IconButton size='small' onClick={() => deleteAnnotation(layer?.name, item, subLayer)} sx={{ visibility: isHovered ? 'visible' : 'hidden' }}>
                <IconComponent iconName='trash-outline' />
            </IconButton>
            {/* Eye icon button */}
            <IconButton size='small' onClick={() => toggleSubChildLayer(layer?.name, item, subLayer)} sx={{ visibility: !isEyeVisible || isHovered ? 'visible' : 'hidden' }}>
                <IconComponent iconName={isEyeVisible ? 'eye-outline' : 'eye-off-outline'} />
            </IconButton>
        </Box>
    </ListItemButton>;
}

/**
 * Renders a single item in the main list of layers (e.g., an aisle or a zone).
 * It can be expanded to show its sub-layers (e.g., lineals).
 * @param {object} props - The component props.
 * @param {object} props.layer - The parent layer data.
 * @param {string} props.item - The name of the item to render.
 * @param {function} props.toggleChildLayer - Function to toggle the visibility of this item.
 * @param {function} props.setDialogEdit - Function to open the edit dialog.
 * @param {function} props.deleteAnnotation - Function to delete the annotation.
 */
function LayerChildItem({ layer, item, toggleChildLayer, setDialogEdit, deleteAnnotation, toggleSubChildLayer }) {
    const [isHovered, setIsHovered] = useState(false);
    const [open, setOpen] = useState(false);
    const sortedSubLayers = useMemo(() => (Object.keys(layer?.layers?.[item]?.subLayers || {}).sort(natsort({ insensitive: true }))), [layer, item]);
    const handleEdit = useCallback(() => {
        setDialogEdit({
            layer: layer?.name,
            item: item,
            data: layer?.layers[item],
        });
        setOpen(true);
    }, [layer, item, setDialogEdit]);

    const { t } = useTranslation();
    const theme = useTheme();
    const hasWarning = useMemo(() => layer?.layers[item]?.warning, [layer, item]);

    return (
        <>
            <ListItemButton sx={{ pl: 4 }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                <IconButton size='small' onClick={(e) => {
                    e.stopPropagation();
                    setOpen(prev => !prev);
                }} sx={{ visibility: Object.keys(layer?.layers[item]?.subLayers || {}).length > 0 ? 'visible' : 'hidden', mr: 1 }}>
                    <IconComponent iconName={open ? 'chevron-up-outline' : 'chevron-down-outline'} />
                </IconButton>
                {/* Aisle name */}
                <ListItemText primary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant='body2' sx={{ fontStyle: hasWarning ? 'italic' : 'normal', textWrap: 'nowrap' }}>{item}</Typography>
                        {layer?.layers[item]?.warning_old_name && <Typography variant='body2' sx={{ color: 'gray.main', textWrap: 'nowrap' }}>
                            {`| ${t('overseer_app.layouts.previous', 'Previously')} ${layer?.layers[item]?.warning_old_name}`}
                        </Typography>}
                        {hasWarning && <Tooltip title={t('overseer_app.layouts.needs_review', 'Needs review')} arrow>
                            <span><IconComponent iconName='warning-outline' style={{ color: theme.palette.error.main }} /></span>
                        </Tooltip>}
                    </Box>
                } />
                {/* Icons */}
                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                    {/* Edit icon button */}
                    {!NO_EDITABLE_LAYERS.includes(layer?.name) && (
                        <IconButton size='small' onClick={handleEdit} sx={{ visibility: isHovered ? 'visible' : 'hidden' }}>
                            <IconComponent iconName='create-outline' />
                        </IconButton>
                    )}
                    {/* Trash icon button */}
                    <IconButton size='small' onClick={() => deleteAnnotation(layer?.name, item)} sx={{ visibility: isHovered ? 'visible' : 'hidden' }}>
                        <IconComponent iconName='trash-outline' />
                    </IconButton>
                    {/* Eye icon button */}
                    <IconButton size='small' onClick={() => toggleChildLayer(layer?.name, item)} sx={{ visibility: !layer?.layers[item]?.visible || isHovered ? 'visible' : 'hidden' }}>
                        <IconComponent iconName={layer?.layers[item]?.visible ? 'eye-outline' : 'eye-off-outline'} />
                    </IconButton>
                </Box>
            </ListItemButton>
            {/* Sub-list */}
            {layer?.layers[item]?.subLayers && <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding dense>
                    {sortedSubLayers?.map((subLayer) => (
                        <LayerChildChildItem key={subLayer} layer={layer} item={item} subLayer={subLayer} deleteAnnotation={deleteAnnotation} setDialogEdit={setDialogEdit} toggleSubChildLayer={toggleSubChildLayer} />
                    ))}
                </List>
            </Collapse>}
        </>
    );
}

/**
 * Renders a top-level layer category (e.g., 'capture', 'zones').
 * It can be expanded to show its children items.
 * @param {object} props - The component props.
 * @param {object} props.layer - The layer data object.
 * @param {object} props.toggles - An object containing toggle functions for different levels.
 */
function LayerItem({ layer, toggles, setDialogEdit, deleteAnnotation }) {
    const [open, setOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const sortedChildrenLayers = useMemo(() => (Object.keys(layer?.layers || {}).sort(natsort({ insensitive: true }))), [layer]);
    const { t } = useTranslation();
    const theme = useTheme();
    const existsWarnings = useMemo(() => Object.values(layer?.layers || {}).filter(layer => layer?.warning).length, [layer]);
    return (
        <>
            <ListItemButton key={layer?.name} dense onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                <IconButton size='small' onClick={(e) => {
                    e.stopPropagation();
                    setOpen(prev => !prev);
                }} sx={{ visibility: Object.keys(layer?.layers || {}).length > 0 ? 'visible' : 'hidden', mr: 1 }}>
                    <IconComponent iconName={open ? 'chevron-up-outline' : 'chevron-down-outline'} />
                </IconButton>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <ListItemText primary={layer?.name} />
                    {existsWarnings > 0 && <Tooltip title={t('overseer_app.layouts.needs_review_count', 'Needs review {{count}} items', { count: existsWarnings })} arrow>
                        <span><IconComponent iconName='warning-outline' style={{ color: theme.palette.error.main }} /></span>
                    </Tooltip>}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, visibility: isHovered || !layer?.visible ? 'visible' : 'hidden', ml: 'auto' }}>
                    <IconButton size='small' onClick={(e) => {
                        e.stopPropagation();
                        toggles?.parent(layer?.name);
                    }}>
                        <IconComponent iconName={layer?.visible ? 'eye-outline' : 'eye-off-outline'} />
                    </IconButton>
                </Box>
                {layer?.protected && <Tooltip title={t('overseer_app.layouts.protected_layer', 'Protected layer')} arrow>
                    <span><IconComponent iconName='shield-outline' /></span>
                </Tooltip>}
            </ListItemButton>
            {layer?.layers && <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding dense>
                    {sortedChildrenLayers?.map((item) => (
                        <LayerChildItem key={item} layer={layer} item={item} toggleChildLayer={toggles?.child} setDialogEdit={setDialogEdit} deleteAnnotation={deleteAnnotation} toggleSubChildLayer={toggles?.subChild} />
                    ))}
                </List>
            </Collapse>}
        </>
    );
}

/**
 * The main sidebar component that displays the hierarchical list of layout layers.
 * @param {object} props - The component props.
 * @param {object} props.layers - The structured object of all layers.
 * @param {object} props.toggles - An object with functions to toggle layer visibility.
 * @param {function} props.setDialogEdit - Function to open the edit dialog.
 * @param {function} props.deleteAnnotation - Function to delete an annotation.
 */
function LayersSidebar({ layers, toggles, setDialogEdit, deleteAnnotation }) {
    const emptyLayers = useMemo(() => (Object.keys(layers || {}).filter(layer => Object.keys(layers[layer]?.layers || {}).length === 0)).sort(natsort({ insensitive: true })), [layers]);
    const sortedLayers = useMemo(() => (
        [...Object.keys(layers || {}).filter(layer => Object.keys(layers?.[layer]?.layers || {}).length > 0).sort(natsort({ insensitive: true })), ...emptyLayers]
    ), [layers, emptyLayers]);

    const { storeMapLayout } = useSelector(state => state.layouts);
    const { t } = useTranslation();
    const confirmRef = useRef(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [layerToDelete, setLayerToDelete] = useState(null);

    // Opens a confirmation dialog before deleting an annotation.
    const handleDeleteAnnotation = useCallback(async (layer, annotation, subAnnotation = null) => {
        setLayerToDelete({ layer, annotation, subAnnotation });
        setConfirmOpen(true);
        const confirm = await new Promise(resolve => confirmRef.current = resolve);
        if (confirm) {
            deleteAnnotation(layer, annotation, subAnnotation);
        }
    }, [deleteAnnotation]);

    const handleConfirm = useCallback(() => {
        setConfirmOpen(false);
        confirmRef.current(true);
    }, []);

    const handleCancel = useCallback(() => {
        setConfirmOpen(false);
        confirmRef.current(false);
    }, []);

    return storeMapLayout?.id ? (
        <>
            <List>
                {sortedLayers.map(layer => (
                    <LayerItem key={layer} layer={layers[layer]} toggles={toggles} setDialogEdit={setDialogEdit} deleteAnnotation={handleDeleteAnnotation} />
                ))}
            </List>
            {/* Confirmation Dialog for Deletion */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>{t('overseer_app.layouts.delete_annotation', 'Delete annotation')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {boldString(layerToDelete?.layer === POSES_LAYER ? t('overseer_app.layouts.delete_pose_text', 'Are you sure you want to delete this pose? This action will **remove all robot’s** poses (base, exit_jail and dock).') : t('overseer_app.layouts.delete_annotation_text', 'Are you sure you want to delete this annotation? This action **cannot be undone**, and all related references will also be removed.'))}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} color="secondary">{t('overseer_app.layouts.cancel', 'Cancel')}</Button>
                    <Button onClick={handleConfirm} variant="contained" color="primary">{t('overseer_app.layouts.confirm', 'Confirm')}</Button>
                </DialogActions>
            </Dialog>
        </>
    ) : <FallbackInformation>{t('overseer_app.layouts.error_getting_store_map', 'Error getting store map')}</FallbackInformation>;
}

export default memo(LayersSidebar);
